import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Trash2, ShoppingBag, Heart, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { useCartWishlist } from '@/context/CartWishlistContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productMedia, setProductMedia] = useState({});
  const navigate = useNavigate();
  const { refreshCounts, updateCartCount, updateWishlistCount } = useCartWishlist();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      console.log(response.data)
      setCartItems(response.data);
      
      // Fetch media for all products
      response.data.forEach(item => {
        if (item.product_id) {
          fetchProductMedia(item.product_id);
        }
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to view your cart');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProductMedia = async (productId) => {
    // Skip if already fetched
    if (productMedia[productId]) return;

    try {
      const response = await axios.get(`${API}/products/${productId}/media`);
      if (response.data.success && response.data.media.length > 0) {
        const mediaList = response.data.media;
        const primary = mediaList.find(media => media.is_primary && media.file_type === 'image');
        const images = mediaList.filter(media => media.file_type === 'image');
        
        setProductMedia(prev => ({
          ...prev,
          [productId]: primary || images[0]
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product media:', error);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
      toast.success('Item removed from cart');
      updateCartCount(-1);
      fetchCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const updateQuantity = async (itemId, newQuantity, currentQuantity, productStock) => {
    // Prevent quantity from going below 1
    if (newQuantity < 1) {
      toast.error('Quantity cannot be less than 1');
      return;
    }

    // Prevent quantity from exceeding stock
    if (newQuantity > productStock) {
      toast.error(`Only ${productStock} items available in stock`);
      return;
    }

    try {
      // Optimistically update UI
      setCartItems(prev => 
        prev.map(item => 
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        )
      );

      // Update cart count context
      const quantityDiff = newQuantity - currentQuantity;
      updateCartCount(quantityDiff);

      // Update backend - Using PUT with quantity as query parameter
      await axios.put(
        `${API}/cart/${itemId}?quantity=${newQuantity}`,
        {},
        { withCredentials: true }
      );

    } catch (error) {
      // Revert on error
      fetchCart();
      refreshCounts();
      
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update quantity');
      }
    }
  };

  const moveToWishlist = async (productId, itemId) => {
    try {
      // Optimistically update UI
      setCartItems(prev => prev.filter(item => item.id !== itemId));
      updateCartCount(-1);
      updateWishlistCount(1);

      await axios.post(`${API}/wishlist/move-from-cart/${productId}`, {}, { withCredentials: true });
      toast.success('Moved to wishlist');
    } catch (error) {
      // Revert on error
      fetchCart();
      refreshCounts();
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to move to wishlist');
      }
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.product?.discount_price || item.product?.price || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="loading">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="cart-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-8" data-testid="cart-title">
            Shopping <span className="accent-red">Cart</span>
          </h1>

          {cartItems.length > 0 ? (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {cartItems.map(item => {
                  const media = productMedia[item.product_id];
                  const imageUrl = media?.file_url || item.product?.image_url;

                  return (
                    <div key={item.id} className="glass-effect p-6 rounded-xl flex items-center space-x-6" data-testid={`cart-item-${item.id}`}>
                      <Link to={`/product/${item.product_id}`}>
                        <img 
                          src={imageUrl} 
                          alt={item.product?.name} 
                          className="w-24 h-24 object-cover rounded-lg hover:opacity-80 transition cursor-pointer"
                        />
                      </Link>
                      <div className="flex-1">
                        <Link to={`/product/${item.product_id}`}>
                          <h3 className="text-xl font-semibold mb-1 hover:text-red-500 transition cursor-pointer" data-testid={`item-name-${item.id}`}>
                            {item.product?.name}
                          </h3>
                        </Link>
                        <p className="text-sm text-gray-400 mb-2">{item.product?.category} • {item.product?.type}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl font-bold accent-red" data-testid={`item-price-${item.id}`}>
                            ₹{(item.product?.discount_price || item.product?.price).toLocaleString()}
                          </p>
                          {item.product?.discount_price && (
                            <p className="text-gray-400 line-through text-sm">
                              ₹{item.product?.price.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="text-center">
                        <p className="text-sm text-gray-400 mb-2">Quantity</p>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1, item.quantity, item.product?.stock)}
                            className="bg-white/5 hover:bg-white/10 border border-white/10 w-8 h-8 rounded-lg font-bold flex items-center justify-center transition"
                            data-testid={`decrease-quantity-${item.id}`}
                            disabled={item.quantity <= 1}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="text-xl font-bold w-10 text-center" data-testid={`item-quantity-${item.id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1, item.quantity, item.product?.stock)}
                            className="bg-white/5 hover:bg-white/10 border border-white/10 w-8 h-8 rounded-lg font-bold flex items-center justify-center transition"
                            data-testid={`increase-quantity-${item.id}`}
                            disabled={item.quantity >= item.product?.stock}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        
                        {item.product?.stock <= 5 && (
                          <p className="text-xs text-yellow-500 mt-1">Only {item.product?.stock} left</p>
                        )}
                      </div>

                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={() => moveToWishlist(item.product_id, item.id)}
                          className="flex items-center space-x-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition text-sm"
                          data-testid={`move-to-wishlist-${item.id}`}
                          title="Move to Wishlist"
                        >
                          <Heart size={18} />
                          <span>Wishlist</span>
                        </button>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="flex items-center space-x-2 px-4 py-2 text-red-500 hover:bg-red-500/10 border border-red-500/30 rounded-lg transition text-sm"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 size={18} />
                          <span>Remove</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="glass-effect p-6 rounded-xl sticky top-24" data-testid="order-summary">
                  <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Subtotal</span>
                      <span className="font-semibold" data-testid="subtotal">₹{calculateTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Shipping</span>
                      <span className="font-semibold text-green-500">FREE</span>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex justify-between">
                        <span className="text-xl font-bold">Total</span>
                        <span className="text-2xl font-bold accent-red" data-testid="total">₹{calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    className="w-full btn-primary py-4"
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </button>

                  <div className="mt-6 p-4 bg-green-600/20 border border-green-600 rounded-lg">
                    <p className="text-sm text-green-400">🎉 Free shipping on all orders!</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20" data-testid="empty-cart">
              <ShoppingBag className="mx-auto mb-6 text-gray-600" size={80} />
              <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
              <p className="text-gray-400 mb-8">Start adding some awesome collectibles!</p>
              <button
                onClick={() => navigate('/shop')}
                className="btn-primary"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Cart;